import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, FormInstance, Input, message, Modal, Select, UploadProps } from "antd";
import axios from "axios";
import moment from "moment";
import {
  checkIsLoading,
  formatNumber,
  getSelectOptions,
  round,
  toHttpFormat,
  useActiveModal,
} from "../../../../lib/utils";
import { useAppSelector } from "../../../..";
import InputNumberFormat from "../../../../components/common/InputNumberFormat";
import { CreateAdjustmentFormData } from "../../model/types/CreateAdjustmentData";
import { useFormData } from "../../../../lib/hooks/useFormData";
import { CreateAdjustmentFiles } from "./CreateAdjustmentFiles/CreateAdjustmentFiles";
import { baseUrl } from "../../../../lib/routes/baseUrl";
import { CreateFileMetaData, FileMetaData } from "../../model/types/FileMetaData";
import { ITenant } from "../../../../lib/types";
import "./CreateAdjustmentsModal.less";

interface ComponentProps {
  visible: boolean;
  isLoading: boolean;
  startLoading: () => void;
  stopLoading: () => void;
  closeModal: () => void;
  submitCreateAdjusment: (values: CreateAdjustmentFormData, reasonContentIds?: number[]) => void;
  createAdjusmentForm: FormInstance<CreateAdjustmentFormData>;
}

export const CreateAdjustmentsModal = (props: ComponentProps) => {
  const {
    visible,
    closeModal,
    submitCreateAdjusment,
    isLoading,
    createAdjusmentForm,
    startLoading,
    stopLoading,
  } = props;

  const { activeModal, setActiveModal } = useActiveModal<"" | "confirmCreate">("");
  const closeConfirmModal = () => {
    setActiveModal("");
  };
  const { payment, paymentAdjustments } = useAppSelector((state) => state.accountPaymentDetails);
  const { dicAdjustmentType, dicAdjustmentReason, dicAdjustmentReasonLoading } = useAppSelector(
    (state) => state.rtkDictionaries
  );
  const tenant = useAppSelector((state) => state.tenants.tenant as ITenant | undefined);

  const [uploadedFiles, setUploadedFiles] = useState<FileMetaData[]>();

  const allowedAdjustmentType = useMemo(
    () =>
      dicAdjustmentType.filter(({ id }) => {
        const allowedAdjustmentTypeIds = [2, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
        return allowedAdjustmentTypeIds.includes(id);
      }),
    [dicAdjustmentType]
  );

  const { formData, onValuesChange, resetFormFields } = useFormData<CreateAdjustmentFormData>({
    form: createAdjusmentForm,
  });

  // Сброс по закрытии модалки
  useEffect(() => {
    if (!visible) {
      resetFormFields();
    }
  }, [visible]);

  const paymentData = useMemo(() => {
    if (payment) {
      const amount = Number(payment.amount) || 0;
      const extraAvailable = Number(payment.extra?.availableAmount) || 0;
      const sumAjusments =
        paymentAdjustments?.reduce(
          (sum, { balanceTransaction }) => sum + balanceTransaction.amount,
          0
        ) || 0;

      const limit = {
        min: Number(round(amount - extraAvailable - sumAjusments)),
        max: Number(round(amount - sumAjusments - 0.01)),
      };

      return {
        limit,
        limitAdj: {
          min: 0.01,
          max: extraAvailable,
        },
        reversalIsAvailable: amount === extraAvailable,
        // reversalIsAvailable: true,
      };
    }
  }, [payment, paymentAdjustments]);

  const deleteFile = (fileId: number) => {
    try {
      const url = `${baseUrl}/dict-api/content/${fileId}`;
      return axios.delete(url);
    } catch (e) {
      message.error(
        <>
          <div>При удалении файла возникла ошибка.</div>
          <div>Пожалуйста, повторите попытку позднее</div>
        </>,
        3
      );
    }
  };

  const uploadFiles: UploadProps["onChange"] = ({ file }) => {
    const { originFileObj } = file;
    if (originFileObj !== undefined && tenant?.id) {
      startLoading();
      const createData: CreateFileMetaData = {
        createDate: toHttpFormat(moment()),
        sourceObjectTypeId: "reason",
        sourceObjectId: null,
        contentName: file.name,
        contentTypeId: 1,
        tenantId: tenant?.id,
      };
      const createMetaUrl = `${baseUrl}/dict-api/content`;
      axios
        .post<FileMetaData>(createMetaUrl, createData)
        .then(({ data }) => {
          if (data) {
            const formData = new FormData();
            formData.append("/file", originFileObj);
            const uploadUrl = `${baseUrl}/dict-api/content/${data.id}/data`;
            axios
              .put(uploadUrl, formData, {
                headers: { "Content-Type": "multipart/form-data" },
              })
              .then(() => {
                message.destroy();
                message.success("Файл добавлен", 3);
                setUploadedFiles((prev) => [...(prev || []), data]);
              })
              .catch((error) => {
                deleteFile(data.id);
                message.destroy();
                message.error(
                  <>
                    <div>При загрузке файла возникла ошибка.</div>
                    <div>Пожалуйста, повторите попытку позднее</div>
                  </>,
                  3
                );
              })
              .finally(stopLoading);
          }
        })
        .catch(() => {
          message.destroy();
          message.error(
            <>
              <div>При загрузке файла возникла ошибка.</div>
              <div>Пожалуйста, повторите попытку позднее</div>
            </>,
            3
          );
        })
        .finally(stopLoading);
    }
  };

  const handleCloseModal = () => {
    if (uploadedFiles?.length) {
      Promise.all(uploadedFiles.map(({ id }) => deleteFile(id)));
      setUploadedFiles(undefined);
    }
    closeModal();
  };

  const toCreate = () => {
    createAdjusmentForm.validateFields().then(() => {
      const { dsc } = createAdjusmentForm.getFieldsValue(["dsc"]);
      if (uploadedFiles?.length || dsc) {
        setActiveModal("confirmCreate");
      } else {
        Modal.error({
          title: "Отсутствуют данные для основания действия",
          content: `Заполните поле "Комментарий" и/или добавьте хотя бы один файл`,
          width: 500,
          okButtonProps: {
            style: {
              width: "108px",
            },
          },
        });
      }
    });
  };

  const handleCreateAdjustment = () => {
    closeConfirmModal();
    createAdjusmentForm.validateFields().then((values) => {
      submitCreateAdjusment(
        values,
        uploadedFiles?.map(({ id }) => id)
      );
      setUploadedFiles(undefined);
    });
  };

  return (
    <>
      <Modal
        className="create-adjustments-modal"
        visible={visible}
        title="Корректировка платежа"
        onCancel={handleCloseModal}
        footer={
          <>
            <Button onClick={handleCloseModal}>Отмена</Button>
            <Button
              type="primary"
              onClick={toCreate}
              loading={isLoading}
              disabled={
                [7, 8].includes(formData?.adjustmentTypeId as number) &&
                !paymentData?.reversalIsAvailable
              }
            >
              Сохранить
            </Button>
          </>
        }
      >
        <Form
          form={createAdjusmentForm}
          labelCol={{ span: 7 }}
          wrapperCol={{ span: 17 }}
          onValuesChange={onValuesChange}
        >
          <Form.Item
            label="Тип корректировки"
            name="adjustmentTypeId"
            rules={[{ required: true, message: "" }]}
          >
            <Select
              options={getSelectOptions({
                list: allowedAdjustmentType,
              })}
              className="create-adjustments-modal__adjustmentTypeId"
            />
          </Form.Item>
          {[2].includes(formData?.adjustmentTypeId as number) && (
            <>
              <Form.Item
                name="adjustmentAmount"
                label="Уменьшить платеж на"
                dependencies={["amount"]}
                rules={[
                  {
                    validator: (_, value) => {
                      const amountNum = Number(formData?.amount);
                      if (amountNum) {
                        return Promise.resolve();
                      }
                      const valueNum = Number(value);
                      if (valueNum) {
                        if (!paymentData) {
                          return Promise.resolve();
                        }
                        if (
                          valueNum < paymentData.limitAdj.min ||
                          valueNum > paymentData.limitAdj.max
                        ) {
                          return Promise.reject();
                        }
                        return Promise.resolve();
                      }
                      return Promise.reject();
                    },
                    message: formData?.adjustmentAmount
                      ? "Недостаточно средств для корректировки"
                      : "",
                  },
                ]}
              >
                <InputNumberFormat
                  decimalScale={2}
                  allowClear
                  placeholder={
                    paymentData?.limitAdj
                      ? `${formatNumber(paymentData.limitAdj.min)} - ${formatNumber(
                          paymentData.limitAdj.max
                        )}`
                      : ""
                  }
                  allowNegative={false}
                  disabled={!!formData?.amount}
                />
              </Form.Item>
              <Form.Item
                name="amount"
                label="Новая сумма"
                dependencies={["adjustmentAmount"]}
                rules={[
                  {
                    validator: (_, value) => {
                      const adjustmentAmountNum = Number(formData?.adjustmentAmount);
                      if (adjustmentAmountNum) {
                        return Promise.resolve();
                      }
                      const valueNum = Number(value);
                      if (valueNum) {
                        if (!paymentData) {
                          return Promise.resolve();
                        }
                        if (valueNum < paymentData.limit.min || valueNum > paymentData.limit.max) {
                          return Promise.reject();
                        }
                        return Promise.resolve();
                      }
                      return Promise.reject();
                    },
                    message: formData?.amount ? "Недостаточно средств для корректировки" : "",
                  },
                ]}
              >
                <InputNumberFormat
                  decimalScale={2}
                  allowClear
                  placeholder={
                    paymentData?.limit
                      ? `${formatNumber(paymentData.limit.min)} - ${formatNumber(
                          paymentData.limit.max
                        )}`
                      : ""
                  }
                  allowNegative={false}
                  disabled={!!formData?.adjustmentAmount}
                />
              </Form.Item>
            </>
          )}
          {[5, 6, 9, 10, 11, 12, 13, 14, 15].includes(formData?.adjustmentTypeId as number) && (
            <Form.Item
              name="adjustmentAmount"
              label="Сумма корректировки"
              rules={[
                {
                  validator: (_, value) => {
                    const valueNum = Number(value);
                    if (valueNum) {
                      if (!paymentData?.limitAdj) {
                        return Promise.resolve();
                      }
                      if (
                        Number(value) < paymentData.limitAdj.min ||
                        Number(value) > paymentData.limitAdj.max
                      ) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    }
                    return Promise.reject();
                  },
                  message: formData?.adjustmentAmount
                    ? "Недостаточно средств для корректировки"
                    : "",
                },
              ]}
            >
              <InputNumberFormat
                decimalScale={2}
                allowClear
                placeholder={
                  paymentData?.limitAdj
                    ? `${formatNumber(paymentData.limitAdj.min)} - ${formatNumber(
                        paymentData.limitAdj.max
                      )}`
                    : ""
                }
                allowNegative={false}
              />
            </Form.Item>
          )}
          {[7, 8].includes(formData?.adjustmentTypeId as number) && (
            <Form.Item wrapperCol={{ offset: 7 }}>
              {paymentData?.reversalIsAvailable && <>Платеж будет сторнирован полностью</>}
              {!paymentData?.reversalIsAvailable && (
                <div style={{ color: "red" }}>
                  <div>Сторнирование платежа невозможно.</div>
                  <div>Перед сторнированием платежа</div>
                  <div>необходимо снять все отметки оплаты</div>
                </div>
              )}
            </Form.Item>
          )}
          <div className="create-adjustments-modal__actBase">Основание действия</div>
          <Form.Item
            label="Тип основания"
            name="adjustmentReasonId"
            rules={[
              {
                required: true,
                message: "",
              },
            ]}
          >
            <Select
              options={getSelectOptions({
                list: dicAdjustmentReason,
              })}
              loading={checkIsLoading(dicAdjustmentReasonLoading)}
            />
          </Form.Item>
          <Form.Item name="dsc" label="Комментарий">
            <Input.TextArea placeholder="Введите текст" draggable={false} />
          </Form.Item>
          <Form.Item
            label={
              <div>
                <div>Прикрепленные</div>
                <div>документы</div>
              </div>
            }
          >
            <CreateAdjustmentFiles
              deleteFile={deleteFile}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              isLoading={isLoading}
              startLoading={startLoading}
              stopLoading={stopLoading}
              uploadFiles={uploadFiles}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        className="confirm-create-adjustments-modal"
        visible={activeModal === "confirmCreate"}
        title="Создание корректировки"
        onCancel={closeConfirmModal}
        footer={
          <>
            <Button onClick={closeConfirmModal}>Отмена</Button>
            <Button type="primary" onClick={handleCreateAdjustment}>
              Далее
            </Button>
          </>
        }
      >
        <div style={{ textAlign: "center" }}>Будет зарегистрирована корректировка к платежу.</div>
        <div style={{ textAlign: "center" }}>Вы подтверждаете действие?</div>
      </Modal>
    </>
  );
};
